import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ProductCard } from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { CATALOG } from '@/constants/testIds';
import { toast } from 'sonner';

export default function Catalog() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = {};
    sp.forEach((v, k) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get('/products', { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [sp]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { api.get('/categories').then(({ data }) => setCategories(data.categories)); }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    setSp(next);
  };

  const onAdd = (p) => { add(p); toast.success(`${p.name} agregado al carrito`); };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Catálogo</div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
            {sp.get('q') ? `Resultados: "${sp.get('q')}"` : 'Todos los repuestos'}
          </h1>
          {(sp.get('vehicle_brand') || sp.get('vehicle_model') || sp.get('vehicle_year')) && (
            <div data-testid="catalog-vehicle-chips" className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-bold uppercase tracking-[0.2em] text-slate-500">Vehículo:</span>
              {['vehicle_brand', 'vehicle_model', 'vehicle_year'].map((k) =>
                sp.get(k) ? (
                  <button
                    key={k}
                    onClick={() => setParam(k, '')}
                    className="inline-flex items-center gap-1 border border-orange-500 bg-orange-50 px-2 py-1 text-orange-700 hover:bg-orange-100"
                  >
                    {sp.get(k)}
                    <span className="text-orange-500" aria-hidden>×</span>
                  </button>
                ) : null,
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
          {/* Filters */}
          <aside className="border border-slate-200 bg-white p-5 h-fit space-y-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Categoría</div>
              <Select
                value={sp.get('category') || 'all'}
                onValueChange={(v) => setParam('category', v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid={CATALOG.filterCategory} className="rounded-none">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Marca</div>
              <Input
                data-testid={CATALOG.filterBrand}
                defaultValue={sp.get('brand') || ''}
                onBlur={(e) => setParam('brand', e.target.value)}
                placeholder="Ej. Bosch"
                className="rounded-none"
              />
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Rango de precio</div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  data-testid={CATALOG.filterMinPrice}
                  type="number"
                  defaultValue={sp.get('min_price') || ''}
                  onBlur={(e) => setParam('min_price', e.target.value)}
                  placeholder="Mín"
                  className="rounded-none"
                />
                <Input
                  data-testid={CATALOG.filterMaxPrice}
                  type="number"
                  defaultValue={sp.get('max_price') || ''}
                  onBlur={(e) => setParam('max_price', e.target.value)}
                  placeholder="Máx"
                  className="rounded-none"
                />
              </div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Condición</div>
              <Select
                value={sp.get('condition') || 'all'}
                onValueChange={(v) => setParam('condition', v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid="catalog-filter-condition" className="rounded-none">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="original">Original</SelectItem>
                  <SelectItem value="genuine">Genérico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Ciudad del vendedor</div>
              <Input
                data-testid="catalog-filter-city"
                defaultValue={sp.get('city') || ''}
                onBlur={(e) => setParam('city', e.target.value)}
                placeholder="Ej. Bogotá"
                className="rounded-none"
              />
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Compatibilidad vehículo</div>
              <Input
                data-testid="catalog-filter-vehicle"
                defaultValue={sp.get('vehicle') || ''}
                onBlur={(e) => setParam('vehicle', e.target.value)}
                placeholder="Ej. Mazda 3"
                className="rounded-none"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="catalog-filter-instock"
                type="checkbox"
                checked={sp.get('in_stock') === 'true'}
                onChange={(e) => setParam('in_stock', e.target.checked ? 'true' : '')}
                className="h-4 w-4"
              />
              <span className="text-sm text-slate-700 font-semibold">Disponibilidad inmediata</span>
            </label>

            <Button
              data-testid={CATALOG.applyFiltersBtn}
              onClick={fetchData}
              className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
            >
              Aplicar filtros
            </Button>
            <button
              onClick={() => setSp(new URLSearchParams())}
              className="w-full text-xs text-slate-500 hover:text-slate-900 underline"
            >
              Limpiar filtros
            </button>
          </aside>

          <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                {loading ? 'Cargando...' : `${items.length} productos encontrados`}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ordenar por</span>
                <Select
                  value={sp.get('sort') || 'newest'}
                  onValueChange={(v) => setParam('sort', v === 'newest' ? '' : v)}
                >
                  <SelectTrigger data-testid="catalog-sort" className="rounded-none w-[200px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Más recientes</SelectItem>
                    <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
                    <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
                    <SelectItem value="name">Nombre (A→Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((p) => <ProductCard key={p.product_id} product={p} onAdd={onAdd} />)}
              {!loading && items.length === 0 && (
                <div className="col-span-full py-16 text-center text-slate-500">
                  No se encontraron productos. Ajusta los filtros.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
